/**
 * Menu Builder Types
 * 
 * Type definitions for the Visual Menu Builder feature.
 * All block types, props, and layout structures.
 */

// ============================================================================
// Block Types
// ============================================================================

/**
 * All supported block types in the menu builder
 */
export type BlockType = 
  | 'header'        // Logo, cover photo, restaurant name
  | 'category'      // Menu category with items
  | 'featured'      // Featured items carousel
  | 'banner'        // Promotional image/CTA
  | 'text'          // Rich text block (story, announcement)
  | 'divider'       // Visual separator
  | 'spacer'        // Empty space
  | 'search'        // Search bar widget
  | 'category-navigation'; // Sticky category navigation

// ============================================================================
// Block Props
// ============================================================================

/**
 * Props for Header block
 */
export interface HeaderBlockProps {
  variant: 'simple' | 'with-cover' | 'minimal' | 'logo-only';
  showLogo: boolean;
  showRestaurantName: boolean;
  showShareButton: boolean;
  customHeight?: number;
  backgroundColor?: string;
  overlayOpacity?: number;
  textColor?: string;
  logoPosition?: 'left' | 'center' | 'right';
  textAlignment?: 'left' | 'center' | 'right';
  logoSize?: 'sm' | 'md' | 'lg' | 'xl' | 'custom';
  customLogoSize?: number;
  logoPadding?: number;
}

/**
 * Props for Category block
 * IMPORTANT: categoryId references an existing category - data is fetched fresh
 */
export interface CategoryBlockProps {
  categoryId: string;          // MUST reference existing category
  layout: 'list' | 'grid' | 'carousel';
  columns: 1 | 2 | 3;
  showImages: boolean;
  showDescriptions: boolean;
  showTitle: boolean;
  showPrices: boolean;
  showAvailability: boolean;
  maxItems?: number;           // Optional limit for display
  
  // Visual overrides (optional)
  overrideBackground?: string;
  overridePadding?: 'none' | 'sm' | 'md' | 'lg';
  titleAlignment?: 'left' | 'center' | 'right';
  itemShape?: 'rounded' | 'square' | 'pill' | 'leaf' | 'arch';
  titleFontSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'custom';
  customTitleFontSize?: number;
  titleColor?: string;
  
  // Item text colors
  itemNameColor?: string;
  itemDescriptionColor?: string;
  itemPriceColor?: string;
}

/**
 * Props for Featured Items block
 * IMPORTANT: itemIds reference existing menu items - data is fetched fresh
 */
export interface FeaturedBlockProps {
  itemIds: string[];           // MUST reference existing menu items
  layout: 'carousel' | 'grid';
  autoplay: boolean;
  interval: number;            // seconds
  showTitle: boolean;
  title?: string;
  titleAlignment?: 'left' | 'center' | 'right';
  columns?: 1 | 2 | 3;
}

/**
 * Props for Banner block
 */
export interface GraphicalArt {
  id: string;
  imageUrl: string;
  positionX: number; // % from left
  positionY: number; // % from top
  width: number; // % of container width
  rotation?: number; // degrees
  opacity?: number; // 0-1
}

export interface BannerBlockProps {
  imageUrl: string;
  linkType: 'none' | 'category' | 'external' | 'item';
  linkTarget?: string;
  aspectRatio: '16:9' | '4:3' | '3:2' | '2:1' | '1:1' | '9:16' | 'auto';
  overlayText?: string;
  overlayPosition?: 'top' | 'center' | 'bottom';
  overlayColor?: string;
  overlayOpacity?: number;
  height?: number;
  borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  graphicalArts?: GraphicalArt[];
}

/**
 * Props for Text block
 */
export interface TextBlockProps {
  content: string;             // Markdown/HTML supported
  contentAr?: string;          // Arabic content (optional)
  alignment: 'left' | 'center' | 'right';
  fontSize: 'sm' | 'md' | 'lg' | 'xl' | 'custom';
  customFontSize?: number;     // Custom font size in pixels
  textColor?: string;
  backgroundColor?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
}

/**
 * Props for Divider block
 */
export interface DividerBlockProps {
  style: 'solid' | 'dashed' | 'dotted' | 'none';
  thickness: 1 | 2 | 3 | 4;
  color?: string;
  spacing: 'none' | 'sm' | 'md' | 'lg';
  width?: 'full' | '3/4' | '1/2' | '1/4';
}

/**
 * Props for Spacer block
 */
export interface SpacerBlockProps {
  height: number;              // in pixels
}

/**
 * Props for Search block
 */
export interface SearchBlockProps {
  sticky: boolean;
  placeholder: string;
  placeholderAr?: string;      // Arabic placeholder
  showCategoryFilter: boolean;
  style: 'default' | 'minimal' | 'rounded';
  backgroundColor?: string;
}

/**
 * Props for Category Navigation block
 */
export interface CategoryNavigationBlockProps {
  sticky: boolean;
  size: 'sm' | 'md' | 'lg';
  style: 'pills' | 'chips' | 'buttons';
  showIcons: boolean;
  showNames: boolean;
  iconSize: 'sm' | 'md' | 'lg';
  scrollToCategory: boolean;
  backgroundColor?: string;
  activeColor?: string;
}

// ============================================================================
// Block Props Map
// ============================================================================

/**
 * Map block types to their props for type safety
 */
export interface BlockPropsMap {
  header: HeaderBlockProps;
  category: CategoryBlockProps;
  featured: FeaturedBlockProps;
  banner: BannerBlockProps;
  text: TextBlockProps;
  divider: DividerBlockProps;
  spacer: SpacerBlockProps;
  search: SearchBlockProps;
  'category-navigation': CategoryNavigationBlockProps;
}

/**
 * Union type of all block props
 */
export type AnyBlockProps = BlockPropsMap[BlockType];

// ============================================================================
// Menu Block
// ============================================================================

/**
 * Base interface for all menu blocks
 */
export interface MenuBlock<T extends BlockType = BlockType> {
  id: string;
  type: T;
  props: BlockPropsMap[T];
  order: number;
}

/**
 * Typed block creators for each type
 */
export type HeaderBlock = MenuBlock<'header'>;
export type CategoryBlock = MenuBlock<'category'>;
export type FeaturedBlock = MenuBlock<'featured'>;
export type BannerBlock = MenuBlock<'banner'>;
export type TextBlock = MenuBlock<'text'>;
export type DividerBlock = MenuBlock<'divider'>;
export type SpacerBlock = MenuBlock<'spacer'>;
export type SearchBlock = MenuBlock<'search'>;
export type CategoryNavigationBlock = MenuBlock<'category-navigation'>;

/**
 * Union type of all block types
 */
export type AnyMenuBlock = 
  | HeaderBlock 
  | CategoryBlock 
  | FeaturedBlock 
  | BannerBlock 
  | TextBlock 
  | DividerBlock 
  | SpacerBlock 
  | SearchBlock
  | CategoryNavigationBlock;

// ============================================================================
// Menu Builder Layout
// ============================================================================

/**
 * Global theme overrides that apply to entire menu
 */
export interface GlobalThemeOverrides {
  // Background
  backgroundType?: 'solid' | 'gradient' | 'image' | 'pattern';
  backgroundColor?: string; // Used for solid and as fallback
  
  // Gradient
  gradientStart?: string;
  gradientEnd?: string;
  gradientDirection?: string; // e.g., 'to right', '45deg'
  
  // Image
  backgroundImage?: string;
  backgroundSize?: 'cover' | 'contain' | 'auto';
  backgroundPosition?: 'center' | 'top' | 'bottom';
  
  // Pattern
  backgroundPattern?: string; // key from backgroundPatterns.ts
  
  // Typography & Colors
  fontFamily?: string;
  fontWeight?: 'normal' | 'bold';
  primaryColor?: string;
  textColor?: string;
  cardBackground?: string;
  
  // Animation
  blockAnimation?: 'none' | 'fade-in' | 'slide-up' | 'scale-up';

  // Cart Customization
  cartPosition?: 'bottom-right' | 'bottom-left' | 'bottom-center';
  cartButtonColor?: string;
  cartIconColor?: string;
  cartButtonStyle?: 'circle' | 'pill' | 'square';
}

/**
 * Complete menu builder layout structure
 * This is what gets stored in the database as JSONB
 */
export interface MenuBuilderLayout {
  version: string;             // Schema version for migrations (e.g., "1.0")
  globalThemeOverrides?: GlobalThemeOverrides;
  blocks: AnyMenuBlock[];
}

// ============================================================================
// Database Types
// ============================================================================

/**
 * Database record for menu builder layouts
 */
export interface MenuBuilderLayoutRecord {
  id: string;
  branch_id: string;
  layout_version: number;
  layout_data: MenuBuilderLayout;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Builder State Types
// ============================================================================

/**
 * View mode in the builder
 */
export type ViewMode = 'mobile' | 'desktop';

/**
 * Drag state for dnd-kit
 */
export interface DragState {
  isDragging: boolean;
  activeBlockId: string | null;
  overBlockId: string | null;
}

/**
 * History entry for undo/redo
 */
export interface HistoryEntry {
  blocks: AnyMenuBlock[];
  timestamp: number;
}

// ============================================================================
// Default Props Generators
// ============================================================================

/**
 * Get default props for a block type
 */
export function getDefaultBlockProps<T extends BlockType>(type: T): BlockPropsMap[T] {
  const defaults: Record<BlockType, AnyBlockProps> = {
    header: {
      variant: 'with-cover',
      showLogo: true,
      showRestaurantName: true,
      showShareButton: true,
      overlayOpacity: 0.3,
      logoPosition: 'center',
      textAlignment: 'center',
      textColor: '#ffffff',
    } as HeaderBlockProps,
    
    category: {
      categoryId: '',
      layout: 'grid',
      columns: 2,
      showImages: true,
      showDescriptions: true,
      showTitle: true,
      showPrices: true,
      showAvailability: true,
      overridePadding: 'md',
      titleAlignment: 'left',
    } as CategoryBlockProps,
    
    featured: {
      itemIds: [],
      layout: 'carousel',
      autoplay: true,
      interval: 5,
      showTitle: true,
      title: 'Featured Items',
      titleAlignment: 'left',
      columns: 2,
    } as FeaturedBlockProps,
    
    banner: {
      imageUrl: '',
      linkType: 'none',
      aspectRatio: '16:9',
      overlayPosition: 'bottom',
      overlayOpacity: 0.5,
      borderRadius: 'md',
    } as BannerBlockProps,
    
    text: {
      content: '',
      alignment: 'left',
      fontSize: 'md',
      padding: 'md',
      fontWeight: 'normal',
    } as TextBlockProps,
    
    divider: {
      style: 'solid',
      thickness: 1,
      spacing: 'md',
      width: 'full',
    } as DividerBlockProps,
    
    spacer: {
      height: 24,
    } as SpacerBlockProps,
    
    search: {
      sticky: true,
      placeholder: 'Search menu...',
      placeholderAr: 'ابحث في القائمة...',
      showCategoryFilter: true,
      style: 'default',
    } as SearchBlockProps,
    
    'category-navigation': {
      sticky: true,
      size: 'md',
      style: 'pills',
      showIcons: true,
      showNames: true,
      iconSize: 'md',
      scrollToCategory: true,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    } as CategoryNavigationBlockProps,
  };

  return defaults[type] as BlockPropsMap[T];
}

// ============================================================================
// Block Metadata
// ============================================================================

/**
 * Metadata for each block type (for toolbox UI)
 */
export interface BlockMetadata {
  type: BlockType;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  icon: string;           // lucide-react icon name
  category: 'layout' | 'content' | 'media' | 'widget';
}

/**
 * All block metadata
 */
export const BLOCK_METADATA: BlockMetadata[] = [
  {
    type: 'header',
    name: 'Header',
    nameAr: 'الترويسة',
    description: 'Restaurant logo and cover photo',
    descriptionAr: 'شعار المطعم وصورة الغلاف',
    icon: 'LayoutTemplate',
    category: 'layout',
  },
  {
    type: 'category',
    name: 'Category',
    nameAr: 'التصنيف',
    description: 'Display items from a menu category',
    descriptionAr: 'عرض عناصر من تصنيف القائمة',
    icon: 'Grid3x3',
    category: 'content',
  },
  {
    type: 'featured',
    name: 'Featured Items',
    nameAr: 'العناصر المميزة',
    description: 'Highlight specific menu items',
    descriptionAr: 'تسليط الضوء على عناصر محددة',
    icon: 'Star',
    category: 'content',
  },
  {
    type: 'banner',
    name: 'Banner',
    nameAr: 'البانر',
    description: 'Promotional image or announcement',
    descriptionAr: 'صورة ترويجية أو إعلان',
    icon: 'Image',
    category: 'media',
  },
  {
    type: 'text',
    name: 'Text Block',
    nameAr: 'نص',
    description: 'Custom text content with formatting',
    descriptionAr: 'محتوى نصي مخصص مع التنسيق',
    icon: 'Type',
    category: 'content',
  },
  {
    type: 'divider',
    name: 'Divider',
    nameAr: 'فاصل',
    description: 'Visual separator between sections',
    descriptionAr: 'فاصل مرئي بين الأقسام',
    icon: 'Minus',
    category: 'layout',
  },
  {
    type: 'spacer',
    name: 'Spacer',
    nameAr: 'مسافة',
    description: 'Empty space for layout control',
    descriptionAr: 'مسافة فارغة للتحكم في التخطيط',
    icon: 'Space',
    category: 'layout',
  },
  {
    type: 'search',
    name: 'Search Bar',
    nameAr: 'شريط البحث',
    description: 'Search and filter menu items',
    descriptionAr: 'البحث وتصفية عناصر القائمة',
    icon: 'Search',
    category: 'widget',
  },
  {
    type: 'category-navigation',
    name: 'Category Navigation',
    nameAr: 'التنقل بين الفئات',
    description: 'Sticky category tabs for quick navigation',
    descriptionAr: 'شريط تنقل لاصق للوصول السريع للفئات',
    icon: 'List',
    category: 'widget',
  },
];

/**
 * Get metadata for a specific block type
 */
export function getBlockMetadata(type: BlockType): BlockMetadata | undefined {
  return BLOCK_METADATA.find(meta => meta.type === type);
}

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Check if a block type is valid
 */
export function isValidBlockType(type: string): type is BlockType {
  return ['header', 'category', 'featured', 'banner', 'text', 'divider', 'spacer', 'search'].includes(type);
}

/**
 * Validate a menu builder layout structure
 */
export function validateLayout(layout: unknown): layout is MenuBuilderLayout {
  if (!layout || typeof layout !== 'object') return false;
  
  const l = layout as Record<string, unknown>;
  
  if (typeof l.version !== 'string') return false;
  if (!Array.isArray(l.blocks)) return false;
  
  for (const block of l.blocks) {
    if (!block || typeof block !== 'object') return false;
    const b = block as Record<string, unknown>;
    if (typeof b.id !== 'string') return false;
    if (typeof b.type !== 'string' || !isValidBlockType(b.type)) return false;
    if (typeof b.order !== 'number') return false;
    if (!b.props || typeof b.props !== 'object') return false;
  }
  
  return true;
}

// ============================================================================
// Empty Layout
// ============================================================================

/**
 * Create an empty layout
 */
export function createEmptyLayout(): MenuBuilderLayout {
  return {
    version: '1.0',
    blocks: [],
  };
}

/**
 * Create a default layout with header
 */
export function createDefaultLayout(): MenuBuilderLayout {
  return {
    version: '1.0',
    blocks: [
      {
        id: 'default-header',
        type: 'header',
        order: 0,
        props: getDefaultBlockProps('header'),
      },
    ],
  };
}
